import React, { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { oapi } from '@/lib/officialsApi';

const EMPTY = { title: '', message: '', start_date: '', end_date: '' };

export const NewRequestDialog = ({ open, orgId, officials, onClose, onDone }) => {
  const [f, setF] = useState(EMPTY);
  const [mode, setMode] = useState('all');
  const [picked, setPicked] = useState(new Set());
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setF(EMPTY); setMode('all'); setPicked(new Set()); } }, [open]);
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));
  const active = officials.filter(o => o.status !== 'inactive');
  const toggle = (id) => setPicked(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  const submit = async () => {
    if (f.start_date && f.end_date && f.end_date < f.start_date) { toast.error('End date must be after the start date'); return; }
    setBusy(true);
    try {
      const body = { ...f, ...(mode === 'pick' ? { official_ids: [...picked] } : {}) };
      const r = await oapi('/officials/availability-requests', { method: 'POST', orgId, body });
      toast.success(`Request sent · ${r.emails_sent} email${r.emails_sent === 1 ? '' : 's'}`);
      onDone();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="new-avail-request-dialog">
        <DialogHeader>
          <DialogTitle className="text-[#00247D]">Request availability</DialogTitle>
          <DialogDescription>Officials get an email with a link to their portal. Replies are tracked here.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label className="text-xs">Title</Label><Input value={f.title} onChange={e => set('title', e.target.value)} placeholder="Spring league games" data-testid="avail-request-title" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label className="text-xs">From</Label><Input type="date" value={f.start_date} onChange={e => set('start_date', e.target.value)} data-testid="avail-request-start" /></div>
            <div className="space-y-1.5"><Label className="text-xs">To</Label><Input type="date" value={f.end_date} onChange={e => set('end_date', e.target.value)} data-testid="avail-request-end" /></div>
          </div>
          <div className="space-y-1.5"><Label className="text-xs">Message (optional)</Label><Textarea rows={2} value={f.message} onChange={e => set('message', e.target.value)} data-testid="avail-request-message" /></div>
          <div className="flex gap-2">
            {[['all', `All active (${active.length})`], ['pick', 'Choose officials']].map(([k, l]) => (
              <button key={k} type="button" onClick={() => setMode(k)} data-testid={`avail-request-mode-${k}`}
                className={`px-3 py-1.5 rounded-full border text-xs font-semibold ${mode === k ? 'bg-[#00247D] border-[#00247D] text-white' : 'bg-white text-slate-600'}`}>{l}</button>
            ))}
          </div>
          {mode === 'pick' && (
            <div className="max-h-44 overflow-y-auto border rounded-lg divide-y">
              {active.map(o => (
                <label key={o.id} className="flex items-center gap-2.5 px-3 py-2 text-sm cursor-pointer hover:bg-slate-50">
                  <Checkbox checked={picked.has(o.id)} onCheckedChange={() => toggle(o.id)} data-testid={`avail-request-pick-${o.id}`} />{o.full_name}
                </label>
              ))}
            </div>
          )}
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={submit} disabled={busy || (mode === 'pick' && !picked.size) || !active.length} className="bg-[#00247D] hover:bg-[#001A5C]" data-testid="avail-request-submit">
              {busy && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}Send request
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
